import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  User as UserIcon,
  ClipboardList,
  FileText,
  BadgeCheck,
  Ban,
  RefreshCcw,
  FileSignature,
  ExternalLink,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const PER_PAGE = 15;

export default function AdminPacientes() {
  const { id } = useParams();
  if (id) return <PacienteDetalle id={id} />;
  return <PacientesLista />;
}

function PacientesLista() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      params.set("page", String(page));
      params.set("per_page", String(PER_PAGE));
      const { data } = await api.get(`/admin/pacientes?${params}`);
      setRows(data.items);
      setTotal(data.meta.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="admin-pacientes-lista">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-[#0F2942]">Personas usuarias</h2>
          <p className="text-xs text-muted-foreground">{total} personas usuarias registradas</p>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            load();
          }}
          className="relative w-full sm:w-72"
        >
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o correo…"
            className="pl-9"
            data-testid="admin-pacientes-search"
          />
        </form>
      </div>

      <div className="mt-5 rounded-xl border border-border bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No hay personas usuarias con esos filtros.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Persona usuaria</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Correo</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Historia</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Consultas</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-border hover:bg-muted/40 cursor-pointer"
                    onClick={() => navigate(`/admin/pacientes/${r.id}`)}
                    data-testid={`paciente-row-${r.id}`}
                  >
                    <td className="px-4 py-3 font-medium text-foreground">
                      {r.nombre_completo}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{r.email}</td>
                    <td className="px-4 py-3">
                      {r.historia_completa ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                          <CheckCircle2 size={12} /> Completa
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                          <XCircle size={12} /> Incompleta
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-foreground">
                      {r.total_consultas}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          r.is_active
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-amber-50 text-amber-800 border border-amber-200"
                        }`}
                      >
                        {r.is_active ? "Activo" : "Desactivado"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-[#2563EB] text-xs font-medium">Ver</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// --------------- DETAIL ---------------

function PacienteDetalle({ id }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperadmin = !!user?.is_superadmin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ nombre_completo: "", email: "" });
  const [confirm, setConfirm] = useState(null); // "deactivate" | "reactivate"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/pacientes/${id}`);
      setData(data);
      setForm({ nombre_completo: data.nombre_completo, email: data.email });
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 404) navigate("/admin/pacientes", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const saveEdits = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (form.nombre_completo !== data.nombre_completo) payload.nombre_completo = form.nombre_completo;
      if (form.email !== data.email) payload.email = form.email;
      if (!Object.keys(payload).length) {
        setEditing(false);
        return;
      }
      const { data: fresh } = await api.put(`/admin/pacientes/${id}`, payload);
      setData(fresh);
      toast.success("Datos actualizados.");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    setSaving(true);
    try {
      const { data: fresh } = await api.put(`/admin/pacientes/${id}`, {
        is_active: !data.is_active,
      });
      setData(fresh);
      toast.success(fresh.is_active ? "Cuenta reactivada." : "Cuenta desactivada.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirm(null);
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando persona usuaria…
      </div>
    );
  }

  const h = data.historia;

  return (
    <div data-testid="admin-paciente-detalle" className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/pacientes")}>
          <ArrowLeft size={14} /> Regresar a personas usuarias
        </Button>
      </div>

      {/* ACUERDO */}
      <AcuerdoStatusCard acuerdo={data.acuerdo} />

      {/* CUENTA */}
      <section className="rounded-xl border border-border bg-white p-6" data-testid="paciente-cuenta">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#0F2942] text-white grid place-items-center">
              <UserIcon size={20} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-[#2563EB]">
                Cuenta
              </div>
              <h2 className="font-display text-xl font-semibold text-[#0F2942] mt-0.5">
                {data.nombre_completo}
              </h2>
              <div className="text-sm text-muted-foreground">{data.email}</div>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                <StatusPill on={data.is_active}>
                  {data.is_active ? "Cuenta activa" : "Cuenta desactivada"}
                </StatusPill>
                <StatusPill on={data.email_verified}>
                  {data.email_verified ? "Correo verificado" : "Correo NO verificado"}
                </StatusPill>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border bg-muted text-foreground">
                  Registro: {new Date(data.created_at).toLocaleDateString("es-MX")}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing((e) => !e)}
              data-testid="paciente-edit-toggle"
            >
              {editing ? "Cancelar" : "Editar"}
            </Button>
            {data.is_active ? (
              <Button
                variant="outline"
                size="sm"
                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={() => setConfirm("deactivate")}
                data-testid="paciente-toggle-active"
              >
                <Ban size={14} /> Desactivar
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => setConfirm("reactivate")}
                className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
                data-testid="paciente-toggle-active"
              >
                <RefreshCcw size={14} /> Reactivar
              </Button>
            )}
          </div>
        </div>

        {editing && (
          <div className="mt-5 grid sm:grid-cols-2 gap-3 border-t border-border pt-4">
            <div>
              <Label>Nombre completo</Label>
              <Input
                value={form.nombre_completo}
                onChange={(e) => setForm({ ...form, nombre_completo: e.target.value })}
                data-testid="paciente-edit-nombre"
              />
            </div>
            <div>
              <Label>Correo</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="paciente-edit-email"
              />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button
                onClick={saveEdits}
                disabled={saving}
                className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
                data-testid="paciente-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Guardando…
                  </>
                ) : (
                  "Guardar cambios"
                )}
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* HISTORIA CLÍNICA */}
      <section
        className="rounded-xl border border-border bg-white p-6"
        data-testid="paciente-historia"
      >
        <div className="flex items-center gap-2">
          <ClipboardList className="text-[#2563EB]" size={16} />
          <h3 className="font-display text-base font-semibold text-[#0F2942]">
            Historia clínica (solo lectura)
          </h3>
        </div>
        {!h ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Esta persona usuaria aún no ha capturado su historia clínica.
          </p>
        ) : (
          <div className="mt-3 grid sm:grid-cols-2 gap-4 text-sm">
            <KV k="Completada" v={h.completada ? "Sí" : "No"} />
            <KV k="Edad" v={h.demograficos?.edad ?? "—"} />
            <KV k="Género" v={renderGenero(h.demograficos)} />
            <KV k="Peso" v={h.demograficos?.peso ? `${h.demograficos.peso} kg` : "—"} />
            <KV k="Altura" v={h.demograficos?.altura ? `${h.demograficos.altura} cm` : "—"} />
            <ListKV label="Alergias" items={h.alergias} />
            <ListKV label="Enfermedades crónicas" items={h.enfermedades_cronicas} />
            <ListKV label="Medicación actual" items={h.medicacion_actual} />
            <ListKV label="Condiciones especiales" items={h.condiciones_especiales} />
            <div className="sm:col-span-2">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                Hábitos
              </div>
              <pre className="text-xs bg-muted/50 rounded-md p-3 whitespace-pre-wrap font-mono text-foreground">
                {h.habitos && Object.keys(h.habitos).length
                  ? JSON.stringify(h.habitos, null, 2)
                  : "—"}
              </pre>
            </div>
          </div>
        )}
      </section>

      {/* CONSULTAS */}
      <section
        className="rounded-xl border border-border bg-white p-6"
        data-testid="paciente-consultas"
      >
        <div className="flex items-center gap-2">
          <BadgeCheck className="text-[#2563EB]" size={16} />
          <h3 className="font-display text-base font-semibold text-[#0F2942]">Consultas</h3>
          <span className="text-xs text-muted-foreground">({data.consultas.length})</span>
        </div>
        {data.consultas.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Sin consultas registradas.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Fecha</th>
                  <th className="text-left px-3 py-2 font-semibold">Estado</th>
                  <th className="text-left px-3 py-2 font-semibold">Pago</th>
                  <th className="text-left px-3 py-2 font-semibold">GPC</th>
                  <th className="text-left px-3 py-2 font-semibold">Resumen</th>
                  {isSuperadmin && <th />}
                </tr>
              </thead>
              <tbody>
                {data.consultas.map((c) => (
                  <tr key={c.id} className="border-t border-border">
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                      {new Date(c.created_at).toLocaleDateString("es-MX")}
                    </td>
                    <td className="px-3 py-2 capitalize">{(c.estado || "").replace("_", " ")}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {c.payment_status || "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {(c.gpc_codes || []).join(", ") || "—"}
                    </td>
                    <td className="px-3 py-2 text-foreground max-w-md truncate">
                      {c.resumen || "—"}
                    </td>
                    {isSuperadmin && (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <SuperadminConsultaBtn consultaId={c.id} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* LABS */}
      <section
        className="rounded-xl border border-border bg-white p-6"
        data-testid="paciente-labs"
      >
        <div className="flex items-center gap-2">
          <FileText className="text-[#2563EB]" size={16} />
          <h3 className="font-display text-base font-semibold text-[#0F2942]">Laboratorios</h3>
          <span className="text-xs text-muted-foreground">({data.laboratorios.length})</span>
        </div>
        {data.laboratorios.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Sin laboratorios cargados.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {data.laboratorios.map((l) => (
              <li
                key={l.id}
                className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2"
                data-testid={`paciente-lab-${l.id}`}
              >
                <div>
                  <div className="text-sm text-foreground">{l.nombre || "(sin nombre)"}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {l.mime} · {(l.file_size / 1024).toFixed(1)} KB ·{" "}
                    {new Date(l.created_at).toLocaleDateString("es-MX")}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isSuperadmin && (
                    <SuperadminLabBtn pacienteId={id} labId={l.id} />
                  )}
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {l.id.slice(0, 8)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <DialogContent data-testid="paciente-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirm === "deactivate" ? "Desactivar cuenta" : "Reactivar cuenta"}
            </DialogTitle>
            <DialogDescription>
              {confirm === "deactivate"
                ? "La persona usuaria no podrá iniciar sesión. Podrás reactivar la cuenta más tarde."
                : "La persona usuaria podrá iniciar sesión nuevamente."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>
              Cancelar
            </Button>
            <Button
              onClick={toggleActive}
              disabled={saving}
              className={
                confirm === "deactivate"
                  ? "bg-amber-600 hover:bg-amber-700 text-white"
                  : "bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
              }
              data-testid="paciente-confirm-btn"
            >
              {saving ? "Procesando…" : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusPill({ on, children }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${
        on
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-amber-50 text-amber-800 border-amber-200"
      }`}
    >
      {on ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
      {children}
    </span>
  );
}

function KV({ k, v }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
        {k}
      </div>
      <div className="text-sm text-foreground mt-0.5">{v ?? "—"}</div>
    </div>
  );
}

const GENERO_LABEL = {
  mujer: "Mujer",
  hombre: "Hombre",
  mujer_trans: "Mujer trans",
  hombre_trans: "Hombre trans",
  intersex: "Intersex",
  nb: "No binario (NB)",
  otro: "Otro",
};

function renderGenero(demograficos) {
  if (!demograficos) return "—";
  const g = demograficos.genero;
  if (!g) return "—";
  const base = GENERO_LABEL[g] || g;
  if (g === "otro" && (demograficos.genero_otro_texto || "").trim()) {
    return `${base} (${demograficos.genero_otro_texto.trim()})`;
  }
  return base;
}

function ListKV({ label, items }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
        {label}
      </div>
      {items && items.length ? (
        <ul className="text-sm text-foreground list-disc pl-5 space-y-0.5">
          {items.map((i, idx) => (
            <li key={idx}>{i}</li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-muted-foreground">—</div>
      )}
    </div>
  );
}


function AcuerdoStatusCard({ acuerdo }) {
  if (!acuerdo) return null;
  const firmado = !!acuerdo.firmado;
  return (
    <section
      className={`rounded-xl border p-4 flex items-start gap-3 ${
        firmado
          ? "border-emerald-200 bg-emerald-50"
          : "border-amber-200 bg-amber-50"
      }`}
      data-testid="paciente-acuerdo-card"
    >
      <div
        className={`w-8 h-8 rounded-md grid place-items-center shrink-0 ${
          firmado ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
        }`}
      >
        <FileSignature size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={`text-[11px] uppercase tracking-widest font-semibold ${
            firmado ? "text-emerald-800" : "text-amber-800"
          }`}
        >
          Acuerdo de Uso · v{acuerdo.version_actual || "1.0"}
        </div>
        <div
          className="text-sm text-foreground mt-0.5"
          data-testid="paciente-acuerdo-status"
        >
          {firmado ? (
            <>
              <b>Firmado</b> el{" "}
              {new Date(acuerdo.fecha).toLocaleDateString("es-MX", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}{" "}
              como <span className="italic">"{acuerdo.nombre_firmado}"</span>.
            </>
          ) : (
            <>
              <b>Pendiente de firma</b> — la persona usuaria verá el gate de
              firma al iniciar sesión.
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function SuperadminLabBtn({ pacienteId, labId }) {
  const [loading, setLoading] = useState(false);
  const openLab = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/pacientes/${pacienteId}/laboratorios/${labId}/url-firmada`,
      );
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("No se pudo generar la URL.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={openLab}
      disabled={loading}
      className="text-[#2563EB] border-[#2563EB]/40"
      data-testid={`superadmin-lab-view-${labId}`}
    >
      {loading ? (
        <Loader2 size={12} className="animate-spin" />
      ) : (
        <ExternalLink size={12} />
      )}
      Ver archivo
    </Button>
  );
}

function SuperadminConsultaBtn({ consultaId }) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const view = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/consultas/${consultaId}`);
      setDetalle(data);
      setOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={view}
        disabled={loading}
        className="text-[#2563EB] border-[#2563EB]/40"
        data-testid={`superadmin-consulta-view-${consultaId}`}
      >
        {loading ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <Sparkles size={12} />
        )}
        Ver IA
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl"
          data-testid="superadmin-consulta-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#2563EB]" />
              Respuesta completa de IA
            </DialogTitle>
            <DialogDescription>
              Acceso superadmin auditado. Cada apertura queda registrada.
            </DialogDescription>
          </DialogHeader>
          {detalle && (
            <div className="text-sm space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="text-xs text-muted-foreground grid sm:grid-cols-2 gap-1">
                <div>Estado: <b className="text-foreground capitalize">{(detalle.estado || "").replace("_", " ")}</b></div>
                <div>Pago: <b className="text-foreground">{detalle.payment_status || "—"}</b></div>
                <div>GPC: <b className="font-mono text-foreground">{(detalle.gpc_codes || []).join(", ") || "—"}</b></div>
                <div>Provider: <b className="text-foreground">{detalle.provider_used || "—"}</b></div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#0F2942]/70 font-semibold">
                  Síntomas capturados
                </div>
                <div className="mt-1 whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 text-foreground">
                  {detalle.sintomas || <span className="text-muted-foreground">(sin datos)</span>}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#2563EB] font-semibold">
                  Respuesta IA (descifrada)
                </div>
                <div
                  className="mt-1 whitespace-pre-wrap rounded-md border border-border bg-white p-3 text-foreground"
                  data-testid="superadmin-consulta-respuesta"
                >
                  {detalle.respuesta_ia || (
                    <span className="text-muted-foreground">
                      (aún no hay respuesta — la consulta puede estar pendiente_pago o requiere_info)
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
